import auth from '../auth';
import {deckReceive} from '../actions/decks';
import {getFolderPermissionForUser} from '../actions/user';
import events from './events';
import * as storage from './storage';
import store from '../redux/store';
import fanOutFromIndex from '../utils/fanOutFromIndex';
import { getObfuscatedEmail } from '../actions/reinvite';
import { getCurrentDeck, getOtp, getSlideIndex } from '../selectors';
import { isPublicEndpoint } from '../utils/url';

const requestCache = new Map();

// This is provided so that the unit tests can reset the cache between tests.
export function clearRequestCache() {
  requestCache.clear();
}

function fetchAndDispatch(actionType, path, options) {
  const key = `${options.method} ${path}`;

  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = fetch(new URL(path, window.env.TYCHO_URL).href, options)
    .then(res => {
      if (res.ok) {
        return res.json();
      }

      const error = new Error(res.statusText);
      error.res = res;

      requestCache.delete(key);

      throw error;
    })
    .then(payload => {
      if (Array.isArray(payload)) {
        payload.forEach(payloadItem => store.dispatch({
          type: actionType,
          payload: payloadItem
        }));
      } else {
        store.dispatch({
          type: actionType,
          payload
        });
      }
      return payload;
    });

  requestCache.set(key, promise);
  setTimeout(() => requestCache.delete(key), 10000);

  return promise;
}

export function request(path, options) {
  return fetch(new URL(path, window.env.TYCHO_URL).href, options).then(res => {
    if (res.ok) {
      return res;
    }

    const error = new Error(res.statusText);
    error.res = res;

    throw error;
  });
}

const toJson = res => res.json();

const buildHeaders = (token, headers = {}) => Object.assign({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`
}, headers);

export const dispatchJson = (actionType, path) => {
  if (isPublicEndpoint(path)) {
    return fetchAndDispatch(actionType, path, {
      method: 'GET',
      headers: {'Content-Type': 'application/json'}
    });
  }

  return auth.getToken().then(token =>
    fetchAndDispatch(actionType, path, {
      method: 'GET',
      headers: buildHeaders(token)
    })
  );
};

export const get = path => {
  if (isPublicEndpoint(path)) {
    return request(path, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  return auth.getToken().then(token => {
    return request(path, {
      method: 'GET',
      headers: buildHeaders(token)
    });
  });
};

export const post = (path, body, headers) => {
  if (isPublicEndpoint(path)) {
    return request(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  return auth.getToken().then(token => {
    return request(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: buildHeaders(token, headers)
    });
  });
};

function loadFolder(folderId) {
  return dispatchJson('FOLDER_RECEIVE', `/v1/folders/${folderId}`).catch(err => {
    console.warn('Error loading folder.', err); // eslint-disable-line no-console
    store.dispatch({
      type: 'ALERT_BAR_SHOW',
      payload: {
        type: 'warning',
        message: 'Problem retrieving folder data. Using defaults.',
        actionType: 'FOLDER_RECEIVE',
        check: ['id', 'folderId']
      }
    });
  });
}

function loadFolderDecks(folderId) {
  return get(`/v1/folders/${folderId}/decks`)
    .then(toJson)
    .then(data => {
      data.results.forEach(deck => deckReceive(deck)(store.dispatch, store.getState));

      const order = data.results.map(deck => deck.id);

      store.dispatch({
        type: 'DECK_ORDER',
        payload: order
      });

      const state = store.getState();
      const currentDeck = getCurrentDeck(state);

      // Now that the order of slides is known, dispatch an action so that
      // currentDeckId gets set with its initial value (based on slideIndex).
      store.dispatch({
        type: 'SET_SEARCH_PARAMS',
        payload: {
          slideOrder: currentDeck.slide_order,
        }
      });
    });
}

function loadSlideComponents(slideId) {
  const urlRoot = typeof slideId === 'string' ? 'pub' : 'v1';
  return dispatchJson('COMPONENT_RECEIVE', `/${urlRoot}/slides/${slideId}/components`).catch(err => {
    console.warn(`Error loading components for slide ${slideId}.`, err); // eslint-disable-line no-console

    store.dispatch({
      type: 'ALERT_BAR_SHOW',
      payload: {
        type: 'error',
        message: 'Unable to load component data. Please try again in a few minutes.',
        actionType: 'COMPONENT_RECEIVE',
        check: ['slide_id', slideId]
      }
    });

    throw err;
  });
}

function loadAllSlideComponentsInSequence(slide_order, deck_id, slideIndex) {
  return fanOutFromIndex(slide_order, slideIndex).reduce((sequence, slide_id, i) => {
    if (i === 0) {
      return sequence
        .then(() => {
          return loadSlideComponents(slide_id);
        })
        .then(components => {
          if (components) {
            storage.patch('first_component_in_deck', {[deck_id]: components[0]});
          }
          return components;
        });
    }
    return sequence.then(() => loadSlideComponents(slide_id));
  }, Promise.resolve());
}

export function loadDeckSlides(deckId) {
  const slideIndex = getSlideIndex(store.getState());

  return dispatchJson('SLIDE_RECEIVE', `/v1/decks/${deckId}/slides`)
    .then(slides => loadAllSlideComponentsInSequence(slides.map(s => s.id), deckId, slideIndex))
    .catch(err => {
      console.warn(`Error loading slides for deck ${deckId}.`, err); // eslint-disable-line no-console

      store.dispatch({
        type: 'ALERT_BAR_SHOW',
        payload: {
          type: 'error',
          message: 'Unable to load Slides. Please try again in a few minutes.',
          actionType: 'SLIDE_RECEIVE',
          check: ['deck_id', deckId]
        }
      });

      return [];
    });
}

export function loadDeck(deckId, folderId) {
  const slideIndex = getSlideIndex(store.getState());

  events.listen({deckId});

  if (folderId) {
    loadFolder(folderId);
  }

  loadDeckSlides(deckId);

  return dispatchJson('DECK_RECEIVE', `/v1/decks/${deckId}`)
    .then(deck => {
      storage.patch('decks', {[deckId]: deck});

      if (!folderId) {
        loadFolder(deck.folder_id);
      }

      // TODO: This loads components, which already happens via loadDeckSlides above.
      // It isn't a problem in the sense that the request caching prevents an extra
      // API call from being made unnecessarily, but it would be better to simplify
      // the logic.
      return loadAllSlideComponentsInSequence(deck.slide_order, deckId, slideIndex);
    })
    .catch(err => {
      console.warn(`Error loading deck ${deckId}.`, err); // eslint-disable-line no-console

      store.dispatch({
        type: 'ALERT_BAR_SHOW',
        payload: {
          type: 'error',
          message: 'Unable to load Deck. Please try again in a few minutes.',
          actionType: 'DECK_RECEIVE',
          check: ['id', deckId]
        }
      });

      throw err;
    });
}

export function loadFolderAndDecks(folderId) {
  return Promise.all([loadFolder(folderId), loadFolderDecks(folderId)])
    .catch(console.error); // eslint-disable-line no-console
}

export function loadPublicDeck(deckIdHash) {
  const slideIndex = getSlideIndex(store.getState());

  dispatchJson('SLIDE_RECEIVE', `/pub/decks/${deckIdHash}/slides`)
    .catch(err => {
      console.error(err); // eslint-disable-line no-console
      store.dispatch({
        type: 'ALERT_BAR_SHOW',
        payload: {
          type: 'error',
          message: 'Unable to load Slides. Please try again in a few minutes.',
          actionType: 'SLIDE_RECEIVE',
          check: ['deck_id', deckIdHash]
        }
      });
      return [];
    });

  dispatchJson('DECK_RECEIVE', `/pub/decks/${deckIdHash}`)
    .catch(err => {
      console.error(err); // eslint-disable-line no-console
      store.dispatch({
        type: 'ALERT_BAR_SHOW',
        payload: {
          type: 'error',
          message: 'Unable to load Deck. Please try again in a few minutes.',
          actionType: 'DECK_RECEIVE',
          check: ['id', deckIdHash]
        }
      });
      return {slide_order: []};
    })
    .then(deck => {
      store.dispatch({
        type: 'FOLDER_RECEIVE',
        payload: {
          id: deck.folder_id,
          options: {}
        }
      });

      return loadAllSlideComponentsInSequence(deck.slide_order, deckIdHash, slideIndex);
    });
}

export function loadInvite(inviteId) {
  return post(`/v1/invites/${inviteId}/view`)
    .then(toJson)
    .then(invite => {
      storage.patch('invites', {[inviteId]: Object.assign({otp: getOtp(store.getState())}, invite)});

      store.dispatch({
        type: 'INVITE_RECEIVE',
        payload: invite
      });

      store.dispatch(getFolderPermissionForUser({
        invite_id: inviteId,
        invitee_user_id: invite.invitee_user_id,
        folder_id: invite.folder_id
      }));

      return loadDeck(invite.deck_id, invite.folder_id);
    })
    .catch(err => {
      console.error(err); // eslint-disable-line no-console
      if (err.message === 'Failed to get token from OTP') {
        return store.dispatch(getObfuscatedEmail(inviteId))
          .then(() => {
            return store.dispatch({
              type: 'ALERT_DIALOG_OPEN',
              payload: {
                type: 'dialog',
                header: 'You need a fresh link to view this Deck',
                template: 'invite_request_dialog',
                inviteId
              }
            });
          });
      }
      return store.dispatch({
        type: 'ALERT_DIALOG_OPEN',
        payload: {
          type: 'error',
          header: 'Problem Opening Invite',
          template: 'invite_open_error'
        }
      });
    });
}

export function getInviteCount(deckId) {
  return get(`/v1/decks/${deckId}/shares/email`)
    .then(toJson)
    .then(({unique_invited_users}) => {
      return store.dispatch({
        type: 'DECK_INVITEE_COUNT',
        payload: {
          deckId,
          unique_invited_users
        }
      });
    });
}

export function getComponentTypes() {
  return get('v1/component_types')
    .then(toJson)
    .then(components => {
      return store.dispatch({
        type: 'COMPONENT_TYPES_RECEIVE',
        payload: components.reduce((acc, component) => {
          acc[component.id] = component;
          return acc;
        }, {})
      });
    });
}

// Request public information about a private link.
// E.g. The ID and name of the deck it shares.
// A valid OTP will either need to be in the URL, or already in local storage,
// in order for further information about the deck to be requested.
export function getPrivateLink(privateLinkId) {
  return get(`pub/private_link/${privateLinkId}`)
    .then(toJson)
    .then(privateLink => {
      const otp = getOtp(store.getState());

      // Put the OTP from the URL (if any) into local storage.
      if (otp) {
        storage.patch(
          'privateLinks',
          {
            [privateLinkId]: Object.assign({otp}, privateLink)
          }
        );
      }

      return privateLink;
    });
}

// When a user views a private link and doesn't have auth for the deck
// they'll be given the chance to submit their email address. If it's
// on the whitelist of allowed emails, they'll receive an email invite
// to the deck.
export function requestPrivateLinkInvite(privateLinkId, email) {
  return post(`pub/private_link/${privateLinkId}/email`, {email})
    .then(toJson);
}

export function logSlideView(inviteId, slideId) {
  return post(`v1/invites/${inviteId}/slides/${slideId}/view`)
    .catch(err => {
      console.error(err); // eslint-disable-line no-console
    });
}

export function getUser() {
  return get('v1/user').then(toJson);
}
